import { Box, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import {
  buildProjectStatusTimeline,
  TIMELINE_STATE_LABEL,
  type StatusAuditLike,
  type TimelineStep,
  type TimelineVisualState,
} from "../../utils/projectStatusTimeline";
import StatusHistoryModal from "./StatusHistoryModal";

interface ProjectStatusTimelineProps {
  createdAt: string | Date;
  currentStatus?: string | null;
  currentJustification?: string | null;
  audits: StatusAuditLike[] | undefined;
}

const DOT_COLOR: Record<TimelineVisualState, string> = {
  pending: "#C3CBD6",
  completed: "#1565C0",
  current: "#1565C0",
  reopened: "#E67E22",
};

const LINE_REACHED = "#1565C0";
const LINE_PENDING = "#D5DBE3";

/**
 * Concluída: círculo azul preenchido. Atual: contorno azul com o número da
 * etapa dentro. Seguinte (pendente): só o contorno cinza. Reaberta: contorno
 * laranja tracejado.
 */
function StepDot({
  state,
  stepNumber,
}: {
  state: TimelineVisualState;
  stepNumber: number;
}) {
  const color = DOT_COLOR[state];
  const isCurrent = state === "current";
  const isCompleted = state === "completed";
  const size = isCurrent ? 22 : isCompleted ? 18 : 16;

  return (
    <Box
      data-testid={isCurrent ? "timeline-current-dot" : undefined}
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: isCompleted ? color : "#fff",
        border: `2px ${state === "reopened" ? "dashed" : "solid"} ${color}`,
        boxSizing: "border-box",
        zIndex: 1,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        color,
      }}
    >
      {isCurrent ? stepNumber : null}
    </Box>
  );
}

function TimelineStepItem({
  step,
  stepNumber,
  isFirst,
  isLast,
  leftReached,
  rightReached,
}: {
  step: TimelineStep;
  stepNumber: number;
  isFirst: boolean;
  isLast: boolean;
  leftReached: boolean;
  rightReached: boolean;
}) {
  const stateLabel = TIMELINE_STATE_LABEL[step.visualState];
  const tooltipTitle = step.justification
    ? `Justificativa: ${step.justification}`
    : "";

  return (
    <Tooltip
      placement="top"
      title={tooltipTitle}
      disableHoverListener={!step.justification}
      enterDelay={200}
    >
      <Box
        data-testid={`timeline-step-${step.statusValue}`}
        data-state={step.visualState}
        data-revisited={step.isRevisited ? "true" : "false"}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          minWidth: 88,
          cursor: step.justification ? "help" : "default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: 24,
            mb: 0.75,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: 2,
              bgcolor: isFirst
                ? "transparent"
                : leftReached
                ? LINE_REACHED
                : LINE_PENDING,
            }}
          />
          <Box>
            <StepDot state={step.visualState} stepNumber={stepNumber} />
          </Box>
          <Box
            sx={{
              flex: 1,
              height: 2,
              bgcolor: isLast
                ? "transparent"
                : rightReached
                ? LINE_REACHED
                : LINE_PENDING,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: 12,
            color: step.visualState === "pending" ? "#9AA4B2" : "#222",
            textAlign: "center",
            lineHeight: 1.2,
            fontWeight: step.visualState === "current" ? 600 : 400,
            whiteSpace: "nowrap",
          }}
        >
          {step.statusLabel}
          {step.isRevisited && (
            <Box
              component="span"
              aria-label="status revisitado"
              sx={{ color: "#E67E22", ml: 0.25 }}
            >
              ↺
            </Box>
          )}
        </Typography>

        {step.durationLabel && (
          <Typography sx={{ fontSize: 11, color: "#667085", mt: 0.25 }}>
            {step.durationLabel}
          </Typography>
        )}

        {stateLabel && (
          <Typography
            sx={{
              fontSize: 10,
              color: DOT_COLOR[step.visualState],
              mt: 0.15,
              fontWeight: 500,
            }}
          >
            {stateLabel}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

const ProjectStatusTimeline = ({
  createdAt,
  currentStatus,
  currentJustification,
  audits,
}: ProjectStatusTimelineProps) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const model = useMemo(
    () =>
      buildProjectStatusTimeline({
        audits,
        createdAt,
        currentStatus,
        currentJustification,
      }),
    [audits, createdAt, currentStatus, currentJustification]
  );

  // Até onde a linha fica azul: etapa atual ou, se o projeto está num status
  // final (concluído), a última etapa alcançada.
  const currentStepIndex = (() => {
    const current = model.steps.findIndex((s) => s.visualState === "current");
    if (current >= 0) return current;
    let lastReached = -1;
    model.steps.forEach((s, i) => {
      if (s.visualState === "completed") lastReached = i;
    });
    return lastReached;
  })();

  if (!createdAt) return null;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          mb: 1,
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            minWidth: 84,
            pt: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#1361A4",
              lineHeight: 1.2,
            }}
          >
            {model.totalDurationLabel}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#8A94A6" }}>
            desde {model.createdAtLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flex: 1,
            minWidth: 0,
          }}
        >
          {model.steps.map((step, index) => (
            <TimelineStepItem
              key={step.statusValue}
              step={step}
              stepNumber={index + 1}
              isFirst={index === 0}
              isLast={index === model.steps.length - 1}
              leftReached={index <= currentStepIndex}
              rightReached={index < currentStepIndex}
            />
          ))}
        </Box>

        <Box
          component="button"
          type="button"
          onClick={() => setHistoryOpen(true)}
          aria-label="Histórico"
          sx={{
            border: 0,
            background: "none",
            cursor: "pointer",
            color: "#1361A4",
            fontSize: 13,
            fontWeight: 600,
            pt: 1.5,
            px: 0.5,
            whiteSpace: "nowrap",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Histórico
        </Box>
      </Box>

      <StatusHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        rows={model.history}
      />
    </>
  );
};

export default ProjectStatusTimeline;
